import React, { useEffect, useState, useRef, useMemo } from 'react';
import { connect } from 'react-redux'
import API from '../../utils/API';
import UserModal from './UserModal';
import RoleModal from './RoleModal';
import RolesDropdown from '../../components/Dropdown/RolesDropdown';
import ConfirmModal from '../../components/Modals/ConfirmModal';
import ImportModal from './ImportModal';
import { useNavigate } from 'react-router-dom';
import Table from '../../components/Table/Table';
import tableCheckbox from '../../components/Inputs/tableCheckbox';
import editIcon from '../../assets/icons/table/edit.svg';
import trashIcon from '../../assets/icons/table/trash.svg';
import constants from '../../utils/constants';
import commonMethods from '../../utils/common-methods';
import { FormattedMessage,  useIntl} from 'react-intl';
import useInfiniteScroll from '../../components/common/useInfiniteScroll';
import UsersAssignSiteModal from './UsersAssignSiteModal';
import commonFunctions from '../../utils/commonFunctions';
import ToastNoSite from '../../components/Toast/ToastNoSite';
import { trackEvent } from '../../utils/analytics';

function UserManagement(props) {
  const navigate = useNavigate();
  const { user } = props;
  const permissionIds = user.permissions.map((permission) => permission.id);

  //Implemented multi-language support
  const intl = useIntl();
  const translation = (id) => intl.formatMessage({ id });

  useEffect(() => {
    if (!permissionIds.includes(constants.permissionIds.userEdit)) {
      navigate('/forbidden');
    }
  }, [navigate, permissionIds]);

  const [selectedUser, setSelectedUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [actionType, setActionType] = useState('');
  const [checkSites, setCheckSites] = useState("");

  const [usersData, setUsersData] = useState([]);
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState({});
  const [sortByCol, setSortByCol] = useState('firstName');
  const [sortByAsc, setSortByAsc] = useState(true);
  const [startIndexForTable, setStartIndexForTable] = useState(0);
  const [searchValue, setSearchValue] = useState('');

  const tableRef = useRef();
  const tableWrapperRef = useRef();

  const loadTable = async (map, resetSelection = true) => {
    const oldMapData = map.data;
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }
    setCheckSites("");
    try {
      const res = await API.getAllUsers();
      // console.log('res.data', res.data)
      res.data.forEach((user) => {
        let resentState = false;
        if (oldMapData[user.id]) {
          resentState = oldMapData[user.id].resent;
        }

        let lastSeenDisplay;
        if (user.status === 0 || user.status === undefined || user.status === null) {
          lastSeenDisplay = resentState ? "Resend Invite" : "Resend Invite"; // Searchable string for 'Resend Invite'
        } else if (user.lastSeen === undefined || user.lastSeen === null) {
          // console.log('User with undefined/null lastSeen:', user.id, user.lastSeen);
          lastSeenDisplay = "Never";
        } else if (new Date() - new Date(user.lastSeen) < 1000 * 30) {
          // console.log('User online check:', user, new Date() - new Date(user.lastSeen));
          lastSeenDisplay = "Online";
        } else {
          // console.log('User lastSeen calculation:', user.id, user.lastSeen, new Date(user.lastSeen));
          lastSeenDisplay = commonMethods.timeSince(new Date(user.lastSeen));
        }
        // 
        let siteNames = '';
        if(user.alertplus_sites && user.alertplus_sites.length > 0){
          siteNames += translation('ALERTS_PLUS') + ": " + user.alertplus_sites.map((site) => site.name).join(', ');
        }
        if(user.sbmm_sites && user.sbmm_sites.length > 0){
          siteNames += (siteNames.length > 0 ? '\n' : '') + translation('MESSAGE_MANAGER') + ": " + user.sbmm_sites.map((site) => site.name).join(', ');
        }
        if(user.sbds_sites && user.sbds_sites.length > 0){
          siteNames += (siteNames.length > 0 ? '\n' : '') + translation('SIGNAGE_PLUS') + ": " + user.sbds_sites.map((site) => site.name).join(', ');
        }

        let siteCheckCompleted = false; 
        const signagePlusRole = Array.isArray(user.roles) && user.roles.some(role =>
          [
            constants.RoleIDs.signageAdmin,
            constants.RoleIDs.signageOwner,
            constants.RoleIDs.signageTeacher
          ].includes(role.id)
        );
        if (signagePlusRole && (!user.sbds_sites || (user.sbds_sites && user.sbds_sites.length === 0))) {
          setCheckSites("FEATURE_ADMIN_NO_SITE");
          siteCheckCompleted = true; 
        }

        if (!siteCheckCompleted) {
          const messageManagerRole = Array.isArray(user.roles) && user.roles.some(role =>
            [
              constants.RoleIDs.editMessageManager,
              constants.RoleIDs.messageManagerFrontDeskView
            ].includes(role.id)
          );
          if (messageManagerRole && (!user.sbmm_sites || (user.sbmm_sites && user.sbmm_sites.length === 0))) {
            setCheckSites("FEATURE_ADMIN_NO_SITE");
            siteCheckCompleted = true;
          }
        }

        map.data[user.id] = {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          lastSeenDisplay,
          lastSeen: user.lastSeen,
          status: user.status,
          phone: user.phone,
          email: user.email,
          roles: user.roles,
          sites: siteNames,
          roleNames: /*user.roles*/(commonFunctions.getRolesSort(user.roles)).map((role) => role.id ? translation(`${role.id}-ROLENAME`) : "").join(', '),
          checked: false,
          resent: resentState,
        };
      });
      map.filteredIds = Object.keys(map.data);
      map.ids = Object.keys(map.data);
      setUsersData(res.data);
      setDataCount(map.ids.length);
      setSelectedIds(map.selectedIds);
      setTotalDataCount(map.ids.length);
      setDataLoaded(true);

      const rolesRes = await API.getRoles();
      //Roles are confined to 1 feature so no need to look further than the 0th index;
      rolesRes.data.sort((a, b) => {
        if(a.features.length > 0 && b.features.length > 0 ){
          return a.features[0].name.localeCompare(b.features[0].name)
        } else {
          return b.features.length - a.features.length
        }
      });
      setRoles(rolesRes.data);
    } catch (err) {
      setError(err.message);
      setDataLoaded(true);
    }
  };

  const dataMap = React.useMemo(() => {
    const map = { data: {}, ids: [], selectedIds: {}, filteredIds: [] };
    loadTable(map);
    return map;
  }, []);

  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndexForTable,
    startIndexForTable,
    pageSize: 100,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
  });

  // Hook for sorting data when data changes or sort column is changed
  useEffect(() => {
    if (dataLoaded) {
      if (searchValue !== '') {
        tableRef.current.search(dataMap);
      } else {
        sortMap(dataMap);
        setData(generateDataSlice(dataMap));
      }
    }
  }, [sortByCol, sortByAsc, dataLoaded, dataMap, usersData]);

  const handleEditClick = (e, id) => {
    e.preventDefault();
    let clickedUser = dataMap.data[id];
    console.log('clickedUser:', clickedUser);

    // Track edit button click
    trackEvent('user_management.edit', { page: 'user_management' }, user);

    setSelectedUser(clickedUser);
  };

  const handleSingleDelete = (e, id) => {
    e.preventDefault();
    let clickedUser = dataMap.data[id];

    // Track delete button click
    trackEvent('user_management.delete', { page: 'user_management' }, user);

    setSelectedUser(clickedUser);
    setActionType('single');
  };

  const handleMassDelete = (e) => {
    e.preventDefault();

    // Track mass delete button click
    trackEvent('user_management.delete_bulk', {
      page: 'user_management',
      selected_count: Object.keys(selectedIds).filter(id => selectedIds[id]).length
    }, user);

    setActionType('multiple');
  };

  const handleDeleteUsers = async () => {
    setSuccess('');
    setError('');

    try {
      if (actionType === 'single') {
        if (selectedUser && !selectedUser.roles.some((role) => role.id === constants.RoleIDs.accountOwner)) {
          await API.deleteUser(selectedUser.id);
          setSuccess(<FormattedMessage id="DELETE_USER_SUCCESS" />);
        } else {
          setError(<FormattedMessage id="DELETE_USER_FAILED" values={{ errorMsg: (<FormattedMessage id="DELETE_OWNER_ERROR" />) }}/>);
        }
      } else {
        const idsToDelete = Object.keys(selectedIds).filter((id) => selectedIds[id]);
        const usersToDelete = idsToDelete
          .map((id) => dataMap.data[id])
          .filter((user) => !user.roles.some((role) => role.id === constants.RoleIDs.accountOwner));

        if (usersToDelete.length === 0) {
          setError(<FormattedMessage id="DELETE_USERS_FAILED" values={{ errorMsg: (<FormattedMessage id="DELETE_USER_NO_ELIGIBLE_USERS" />) }}/>);
          return;
        }

        await Promise.all(usersToDelete.map((user) => API.deleteUser(user.id)));

        setSuccess(<FormattedMessage id="COUNT_USERS_DELETED_SUCCESS" values={{ userCounts: usersToDelete.length }} />);
      }
      closeModal('confirmModal');
    } catch (err) {
      setError(<FormattedMessage id="DELETE_USERS_FAILED" values={{ errorMsg: <b>{err.message}</b> }} />);
    }

    loadTable(dataMap);
  };

  const resendVerification = async (e, row) => {
    e.preventDefault();
    dataMap.data[row.id].resent = true;
    loadTable(dataMap, false);
    setTimeout(() => {
      dataMap.data[row.id].resent = false;
      loadTable(dataMap, false);
    }, 10000);
    try {
      await API.resendUserVerification(row.email);
      setSuccess(<FormattedMessage id="RESENT_TO_EMAIL" values={{ email: <b>{row.email}</b> }} />);
    } catch (err) {
      setError(<FormattedMessage id="RESENT_TO_EMAIL_FAIL" values={{ email: <b>{row.email}</b> }} />);
    }
  };

  const resetUserModalRef = useRef(null);
  const handleResetUserModal = () => {
    if (resetUserModalRef.current) {
      resetUserModalRef.current();
    }
  };

  const resetRoleModalRef = useRef(null);
  const handleResetRoleModal = () => {
    if (resetRoleModalRef.current) {
      resetRoleModalRef.current();
    }
  };

  const resetImportModalRef = useRef(null);
  const handleResetImportModal = () => {
    if (resetImportModalRef.current) {
      resetImportModalRef.current();
    }
  };

  const closeModal = (modalId) => {
    const modal = document.getElementById(modalId);
    if (modal) {
      const closeButton = modal.querySelector('.btn-close, [data-bs-dismiss="modal"]');
      if (closeButton) {
        closeButton.click();
      }
    }
    // Reset modal states after closing the modal
    handleResetUserModal();
    handleResetRoleModal();
  };

  const tableColumnArray = [
    {
      Header: tableCheckbox,
      id: 'checkbox',
      accessor: 'checked',
      Cell: tableCheckbox,
      isVisible: true,
      width: 63,
      maxWidth: 63,
    },
    {
      Header: 'FIRST_NAME',
      id: 'firstName',
      accessor: 'firstName',
      isVisible: true,
    },
    {
      Header: 'LAST_NAME',
      id: 'lastName',
      accessor: 'lastName',
      isVisible: true,
    },
    {
      Header: 'EMAIL',
      id: 'email',
      accessor: 'email',
      isVisible: true,
    },
    {
      Header: 'LAST_SEEN',
      id: 'lastSeen',
      isVisible: true,
      accessor: 'lastSeenDisplay', 
      Cell: ({ value, row }) => {
        const { status, resent, lastSeenDisplay } = row.original;
        if (status === 0 || status === undefined || status === null) {
          if (resent) {
            return <div style={{ color: 'gray', cursor: 'pointer' }}><FormattedMessage id="RESEND_INVITE" defaultMessage='Resend Invite'/></div>;
          }
          return <div onClick={(e) => resendVerification(e, row.original)} style={{ color: '#0070B9', cursor: 'pointer' }}><FormattedMessage id="RESEND_INVITE" defaultMessage='Resend Invite'/></div>;
        } else if (lastSeenDisplay === "Never") {
          return lastSeenDisplay;
        } else if (lastSeenDisplay === "Online") {
          return <div style={{ color: 'green' }}>{lastSeenDisplay}</div>;
        } else {
          return <div style={{ color: 'gray' }}>{lastSeenDisplay}</div>;
        }
      },
    },
    {
      Header: 'ROLES',
      id: 'roleNames',
      isVisible: true,
      accessor: 'roleNames',
    },
    {
      Header: 'SITES',
      id: 'sites',
      isVisible: true,
      accessor: 'sites',
    },
    {
      Header: '',
      id: 'actions',
      isVisible: true,
      Cell: ({ row }) => (
        <div className="actions">
           <button
            className="btn"
            id="editBtn"
            data-bs-toggle="modal"
            data-bs-target="#userModal"
            onClick={(e) => {
              e.stopPropagation(); // Prevent click from propagating to the parent cell            
              if (handleEditClick) handleEditClick(e, row.original.id);
            }}
          >
            <img src={editIcon} className="actionIcon editAction" alt={<FormattedMessage id="EDIT" />} />
          </button>
          <button
            className="btn"
            id="deleteBtn"
            data-bs-toggle="modal"
            data-bs-target="#confirmModal"
            onClick={(e) => {
              e.stopPropagation(); // Prevent click from propagating to the parent cell
              if (handleSingleDelete) handleSingleDelete(e, row.original.id);
            }}
          >
            <img src={trashIcon} className="actionIcon deleteAction" alt={<FormattedMessage id="DELETE" />} />
          </button>
        </div>
      ),
    },
  ];

  const tableColumns = useMemo(() => tableColumnArray, []);

  const buttons = {
    toolbarRightButtons: (
      <>
        <button
          id="addBtn"
          type="button"
          className="btn btn-unfilled"
          data-bs-toggle="modal"
          data-bs-target="#userModal"
          onClick={handleResetUserModal}
        >
          <span><FormattedMessage id="ADD_USER" defaultMessage="Add User" /></span>
        </button>
        <button
          id="importBtn"
          type="button"
          className="btn btn-filled"
          data-bs-toggle="modal"
          data-bs-target="#importUsersModal"
          onClick={handleResetImportModal}
        > 
          <FormattedMessage id="IMPORT_USERS" defaultMessage="Import Users" />
        </button>
      </>
    ),
    footerButtons: (
      <>
       <button
          id="setRolesBtn"
          type="button"
          className="btn"
          data-bs-toggle="modal"
          disabled={Object.keys(selectedIds).length === 0}
          data-bs-target="#setRolesModal"
        >
          <span><FormattedMessage id="SET_ROLES" defaultMessage="Set Roles" /></span>
        </button>
        <button
          id="assignSiteBtn"
          type="button"
          className="btn"
          data-bs-toggle="modal"
          data-bs-target="#usersAssignSiteModal"
          disabled={Object.keys(selectedIds).length === 0}
        >
          <FormattedMessage id="ASSIGN_CMS_SITE" defaultMessage="Assign CMS/Site" />
        </button>
        <button
          id="deleteBtn"
          type="button"
          className="btn deleteBtn"
          data-bs-toggle="modal"
          data-bs-target="#confirmModal"
          disabled={Object.keys(selectedIds).length === 0}
          onClick={handleMassDelete}
        >
          <FormattedMessage id="DELETE" defaultMessage="Delete" />
        </button>
      </>
    ),
  }

  const tableProps = {
    // The tableKey is for task#20542.
    // Each table requires a unique key to prevent preferences from being overwritten.
    tableKey: "users_table",
    pageType: constants.pageType.Users,
    modalId: 'userModal',
    columns: tableColumns,
    data,
    setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount,
    setDataCount,
    totalDataCount,
    setSortBy,
    sortByCol,
    sortByAsc,
    success,
    setSuccess,
    error,
    setError,
    startIndex: startIndexForTable,
    selectedIds,
    setSelectedIds,
    searchValue,
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    handleEditClick,
    handleSingleDelete,
    buttons,
    checkSites,
  };

  const userModalProps = {
    setResetUserModalRef: (resetFunc) => { resetUserModalRef.current = resetFunc; },
    user,
    success,
    error,
    setSuccess,
    setError,
    closeModal,
    selectedUser,
    setSelectedUser,
    loadTable,
    dataMap,
  };

  const confirmModalProps = {
    actionType,
    selectedItem: selectedUser,
    checkedItems: Object.keys(selectedIds),
    handleDelete: handleDeleteUsers,
    closeModal,
    dataType: 'user',
  };

  const roleModalProps = {
    setResetRoleModalRef: (resetFunc) => { resetRoleModalRef.current = resetFunc; },
    roles,
    dataMap,
    loadTable,
    setSuccess,
    modalError,
    setModalError,
    closeModal
  };

  const importModalProps = {
    setResetImportModalRef: (resetFunc) => { resetImportModalRef.current = resetFunc; },
    setSuccess,
    modalError,
    setModalError,
    ImportLoadData: () => loadTable(dataMap),
    closeModal,
  };

  const siteModalProps = {
    // Filter to only include actually selected users (where value is 1)
    selectedUserIds: Object.keys(dataMap.selectedIds).filter(id => dataMap.selectedIds[id] === 1),
    setSuccess,
    setError,
    dataMap,
    loadTable,
    usersData, // Pass raw user data for feature-site prefilling
  };

  const moderationModalProps = {
    setSuccess,
    setError,
  };

  return (
    <>
      <UserModal {...userModalProps} />
      <ConfirmModal {...confirmModalProps} />
      <ImportModal {...importModalProps} />
      <RoleModal {...roleModalProps} />
      <UsersAssignSiteModal {...siteModalProps} />
      <Table {...tableProps} />
      <ToastNoSite checkSites={checkSites} />
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(UserManagement);
